import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, Divider, Link } from '@mui/material';
import { AdminPanelSettings as AdminIcon, Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const NOT_ADMIN = 'This account is not a platform administrator.';
const HEADER_BG = '#0b1220';
const ACCENT = '#f59e0b';

const AdminLogin = () => {
  const [form, setForm] = useState({ login: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, loading: authLoading, login, loginWithProvider, logout } = useAuth();
  const navigate = useNavigate();

  // Already signed in as a platform admin: straight to the console.
  useEffect(() => {
    if (!authLoading && user?.isPlatformAdmin) navigate('/admin', { replace: true });
  }, [user, authLoading, navigate]);

  const finish = async (response) => {
    if (response?.user?.isPlatformAdmin) {
      navigate('/admin', { replace: true });
    } else {
      await logout().catch(() => {});
      setError(NOT_ADMIN);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await finish(await login(form));
    } catch (err) {
      setError(err.message || 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setSubmitting(true);
    setError('');
    try {
      await finish(await loginWithProvider('google'));
    } catch (err) {
      setError(err.message || 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  }

  // Signed in, but as a regular customer user.
  const signedInNonAdmin = user && !user.isPlatformAdmin;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f3f4f6' }}>
      <Box sx={{ bgcolor: HEADER_BG, color: '#fff', py: 3, px: 3, borderBottom: `3px solid ${ACCENT}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AdminIcon sx={{ fontSize: 36, color: ACCENT }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Excubia Platform Admin</Typography>
          <Typography variant="caption" sx={{ color: 'grey.400', letterSpacing: 1, textTransform: 'uppercase' }}>Excubia staff only</Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: 3, pt: 8 }}>
        <Card sx={{ width: '100%', maxWidth: 440, borderTop: `4px solid ${ACCENT}` }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Sign in to the admin console</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use your Excubia staff account. Customer accounts cannot access this area.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {signedInNonAdmin ? (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You are signed in as <strong>{user.email}</strong>. {NOT_ADMIN}
                </Alert>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" onClick={() => navigate('/dashboard')}>Go to the app</Button>
                  <Button variant="outlined" onClick={() => logout()}>Sign out and switch account</Button>
                </Box>
              </Box>
            ) : (
              <>
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField margin="normal" required fullWidth label="Email" name="login" type="email" autoComplete="email" autoFocus
                    value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
                  <TextField margin="normal" required fullWidth label="Password" name="password" type="password" autoComplete="current-password"
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  <Button type="submit" fullWidth variant="contained" disabled={submitting}
                    sx={{ mt: 3, mb: 1, bgcolor: HEADER_BG, '&:hover': { bgcolor: '#1f2937' } }}>
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }}>or</Divider>
                <Button fullWidth variant="outlined" startIcon={<GoogleIcon />} onClick={handleGoogle} disabled={submitting}>
                  Continue with Google
                </Button>
              </>
            )}

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
              Not staff? <Link component={RouterLink} to="/login">Go to the customer sign-in</Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminLogin;
